import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/newsletter", { email });
      toast.success(
        res.data.message === "already subscribed" ? "Vous êtes déjà sur la liste." : "Bienvenue. Vous êtes sur la liste."
      );
      setEmail("");
    } catch {
      toast.error("Inscription impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-white/5 bg-coal">
      <div className="mx-auto max-w-[1700px] px-5 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">Newsletter</p>
            <h3 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl">
              Rejoignez la liste
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
              Early access to drops, private archive sales and studio notes. No noise — only signal.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex max-w-md">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                data-testid="newsletter-email-input"
                className="h-14 flex-1 border border-white/10 bg-ink px-5 font-mono text-xs tracking-wider text-zinc-100 placeholder:text-zinc-600 focus:border-ember focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="flex h-14 items-center gap-2 bg-ember px-6 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-emberhover disabled:opacity-60"
              >
                {loading ? "..." : <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Navigate</p>
            <ul className="mt-6 space-y-3">
              {[
                { to: "/collections", label: "Collections" },
                { to: "/lookbook", label: "Lookbook" },
                { to: "/about", label: "About" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-${l.label.toLowerCase()}-link`}
                    className="text-sm text-zinc-400 transition-colors duration-300 hover:text-ember"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Studio</p>
            <p className="mt-6 text-sm leading-relaxed text-zinc-400">
              14 Rue de la Fidélité
              <br />
              75010 Paris, France
              <br />
              <a href="mailto:malgrelesdifficultess@gmail.com" className="transition-colors duration-300 hover:text-ember">
                malgrelesdifficultess@gmail.com
              </a>
            </p>
            <div className="mt-6 flex gap-3">
              {[Instagram, Twitter, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  aria-label="Social link"
                  className="flex h-10 w-10 items-center justify-center border border-white/10 text-zinc-400 transition-colors duration-300 hover:border-ember hover:text-ember"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden px-5 sm:px-8 lg:px-16">
        <p className="whitespace-nowrap font-blackletter text-[11vw] leading-[1] tracking-tight text-zinc-100 lg:text-[8vw]">
          Malgré Les Difficultés
        </p>
      </div>

      <div className="border-t border-white/5">
        <div className="mx-auto flex max-w-[1700px] flex-col items-start justify-between gap-3 px-5 py-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 sm:flex-row sm:items-center sm:px-8 lg:px-16">
          <span>© 2026 MLD — Paris</span>
          <span>Malgré les difficultés, on avance.</span>
        </div>
      </div>
    </footer>
  );
}
